import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react'

/*
 * Liquid glass.
 * On Chromium we build a per-element displacement map (a rounded-rect "lens" whose rim bends
 * the backdrop inwards) and feed it to an SVG filter used as `backdrop-filter: url(#id)`, with a
 * tiny RGB split for chromatic aberration. Everywhere else it degrades to a frosted blur.
 * Both paths share the `.glass` rim-light + pointer sheen from index.css.
 */

const supportsSvgBackdrop = (() => {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  const chromium = /Chrome\/|Edg\//.test(ua) && !/Firefox|FxiOS|CriOS.*Mobile Safari/.test(ua) && !!window.chrome
  return chromium && CSS.supports('backdrop-filter', 'url(#x)')
})()

const mapCache = new Map()

function buildDisplacementMap(w, h, radius, bezel) {
  const key = `${w}x${h}r${radius}b${bezel}`
  if (mapCache.has(key)) return mapCache.get(key)

  // Work at reduced resolution for big surfaces — the filter stretches it back.
  const k = Math.min(1, Math.sqrt(160000 / (w * h)))
  const cw = Math.max(2, Math.round(w * k))
  const ch = Math.max(2, Math.round(h * k))
  const r = Math.min(radius, w / 2, h / 2) * k
  const b = Math.max(2, bezel * k)

  const canvas = document.createElement('canvas')
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d')
  const img = ctx.createImageData(cw, ch)
  const data = img.data
  const hw = cw / 2
  const hh = ch / 2

  const sdf = (x, y) => {
    const qx = Math.abs(x - hw) - (hw - r)
    const qy = Math.abs(y - hh) - (hh - r)
    const ox = Math.max(qx, 0)
    const oy = Math.max(qy, 0)
    return Math.hypot(ox, oy) + Math.min(Math.max(qx, qy), 0) - r
  }

  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      const i = (y * cw + x) * 4
      const px = x + 0.5
      const py = y + 0.5
      const d = -sdf(px, py) // depth inside the shape
      let dx = 0
      let dy = 0
      if (d > 0 && d < b) {
        const nx = sdf(px + 0.5, py) - sdf(px - 0.5, py)
        const ny = sdf(px, py + 0.5) - sdf(px, py - 0.5)
        const len = Math.hypot(nx, ny) || 1
        // convex "squircle" profile: strong at the rim, easing to zero inside
        const t = 1 - d / b
        const m = Math.pow(t, 2.2)
        dx = (-nx / len) * m
        dy = (-ny / len) * m
      }
      data[i] = 128 + dx * 127
      data[i + 1] = 128 + dy * 127
      data[i + 2] = 128
      data[i + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)
  const url = canvas.toDataURL()
  if (mapCache.size > 60) mapCache.clear()
  mapCache.set(key, url)
  return url
}

const LiquidGlass = forwardRef(function LiquidGlass(
  {
    as: Tag = 'div',
    radius = 24,
    bezel = 18,
    refraction = 46,
    blur = 1.6,
    aberration = 0.07,
    saturate = 1.6,
    liquid = true,
    sheen = true,
    className = '',
    style,
    children,
    onPointerMove,
    onPointerLeave,
    ...rest
  },
  ref,
) {
  const id = `lg${useId().replace(/[:«»]/g, '')}`
  const el = useRef(null)
  const [size, setSize] = useState(null)
  const useLiquid = liquid && supportsSvgBackdrop

  useImperativeHandle(ref, () => el.current)

  useEffect(() => {
    if (!useLiquid || !el.current) return
    let raf = 0
    const ro = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const { width, height } = entry.contentRect
        const box = entry.target.getBoundingClientRect()
        const w = Math.round(box.width || width)
        const h = Math.round(box.height || height)
        if (w > 0 && h > 0) setSize((s) => (s && s.w === w && s.h === h ? s : { w, h }))
      })
    })
    ro.observe(el.current)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [useLiquid])

  const handleMove = useCallback(
    (e) => {
      onPointerMove?.(e)
      if (!sheen || e.pointerType === 'touch') return
      const r = el.current.getBoundingClientRect()
      el.current.style.setProperty('--sheen-x', `${e.clientX - r.left}px`)
      el.current.style.setProperty('--sheen-y', `${e.clientY - r.top}px`)
      el.current.style.setProperty('--sheen-o', '0.2')
      const angle = (Math.atan2(e.clientY - r.top - r.height / 2, e.clientX - r.left - r.width / 2) * 180) / Math.PI + 90
      el.current.style.setProperty('--rim-angle', `${angle}deg`)
    },
    [onPointerMove, sheen],
  )
  const handleLeave = useCallback(
    (e) => {
      onPointerLeave?.(e)
      el.current?.style.setProperty('--sheen-o', '0.1')
    },
    [onPointerLeave],
  )

  const mapUrl = useLiquid && size ? buildDisplacementMap(size.w, size.h, radius, bezel) : null
  const glassStyle = { borderRadius: radius, ...style }
  if (mapUrl) {
    glassStyle.backdropFilter = `url(#${id})`
    glassStyle.WebkitBackdropFilter = `url(#${id})`
  }

  return (
    <Tag ref={el} className={`glass ${className}`} style={glassStyle} onPointerMove={handleMove} onPointerLeave={handleLeave} {...rest}>
      {mapUrl && (
        <svg aria-hidden width="0" height="0" style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}>
          <filter
            id={id}
            x="0"
            y="0"
            width={size.w}
            height={size.h}
            filterUnits="userSpaceOnUse"
            primitiveUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation={blur} result="blurred" />
            <feImage href={mapUrl} x="0" y="0" width={size.w} height={size.h} preserveAspectRatio="none" result="map" />
            <feDisplacementMap in="blurred" in2="map" scale={refraction} xChannelSelector="R" yChannelSelector="G" result="dispR" />
            <feColorMatrix in="dispR" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="r" />
            <feDisplacementMap in="blurred" in2="map" scale={refraction * (1 - aberration)} xChannelSelector="R" yChannelSelector="G" result="dispG" />
            <feColorMatrix in="dispG" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="g" />
            <feDisplacementMap in="blurred" in2="map" scale={refraction * (1 - aberration * 2)} xChannelSelector="R" yChannelSelector="G" result="dispB" />
            <feColorMatrix in="dispB" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="b" />
            <feBlend in="r" in2="g" mode="screen" result="rg" />
            <feBlend in="rg" in2="b" mode="screen" result="rgb" />
            <feColorMatrix in="rgb" type="saturate" values={saturate} />
          </filter>
        </svg>
      )}
      {children}
    </Tag>
  )
})

export default LiquidGlass
export { supportsSvgBackdrop }
